import React, { useState, useEffect } from 'react';
import inventoryApi from '../../../services/inventoryApi';
import { costCenterApi } from '../../../services/financeApi';
import adminAPI from '../../../services/adminApi';
import ConfirmationModal from '../../Common/ConfirmationModal';
import PermissionGuard from '../../Common/PermissionGuard';
import { useAuth } from '../../../contexts/AuthContext';
import { openPurchaseOrderReport } from '../../../utils/openInventoryReport';
import { canPrintPurchaseOrder } from '../../../utils/documentPrintGate';

const PurchaseOrderDetails = ({ open, onClose, order, onOrderUpdate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showWaveOffConfirm, setShowWaveOffConfirm] = useState(false);
  const [costCenterName, setCostCenterName] = useState('');
  const [companyInfo, setCompanyInfo] = useState({});
  const [approvalRequirement, setApprovalRequirement] = useState(null);

  // Which approval level does this order's value fall into, and who may act on it?
  useEffect(() => {
    if (!open || order?.status !== 'draft') { setApprovalRequirement(null); return; }
    let cancelled = false;
    inventoryApi.resolvePOApprovalTier(order?.totals?.grandTotal ?? 0)
      .then((res) => { if (!cancelled) setApprovalRequirement(res?.data || null); })
      .catch(() => { if (!cancelled) setApprovalRequirement(null); });
    return () => { cancelled = true; };
  }, [open, order?.status, order?.totals?.grandTotal]);

  const currentUserId = user?._id || user?.id || '';
  const isSuperAdmin = (typeof user?.primaryRole === 'string' ? user.primaryRole : user?.primaryRole?.roleCode) === 'SUPER_ADMIN'
    || user?.role === 'SUPER_ADMIN';
  const requiredApprovers = approvalRequirement?.tier?.approvers || [];
  // Until the requirement resolves, fall back to the pre-feature behaviour.
  const canApprove = !approvalRequirement?.enabled
    || requiredApprovers.some((a) => String(a.user) === String(currentUserId))
    || (approvalRequirement?.allowSuperAdminOverride && isSuperAdmin);
  const requiredApproverNames = requiredApprovers
    .map((a) => a.userName || a.userEmail)
    .filter(Boolean)
    .join(', ');

  useEffect(() => {
    if (!order?.costCenter) { setCostCenterName(''); return; }
    if (order.costCenterLabel) { setCostCenterName(order.costCenterLabel); return; }
    costCenterApi.getCostCenterById(order.costCenter)
      .then(res => {
        const cc = res.data?.data?.costCenter || res.data?.data || res.data;
        setCostCenterName(cc ? `${cc.code} - ${cc.name}` : order.costCenter);
      })
      .catch(() => setCostCenterName(order.costCenter));
  }, [order?.costCenter, order?.costCenterLabel]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[PurchaseOrderDetails] could not load location info:', e.message);
      }
    })();
  }, [open]);

  const handlePrint = async () => {
    await openPurchaseOrderReport(order, companyInfo);
  };

  if (!order || !open) return null;

  const tabs = [
    {
      id: 'overview',
      name: 'Overview',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'items',
      name: 'Items',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      badge: (order.lineItems || order.items || []).length
    },
    {
      id: 'supplier',
      name: 'Supplier',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      id: 'delivery',
      name: 'Delivery & Terms',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-9 0h10m-9 4h10m0 0v8a2 2 0 01-2 2H7a2 2 0 01-2-2v-8a2 2 0 012-2h10a2 2 0 012 2z" />
        </svg>
      )
    },
    {
      id: 'financial',
      name: 'Financial',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    }
  ];

  const confirmApproveOrder = async () => {
    setLoading(true);
    setError('');
    try {
      await inventoryApi.approvePurchaseOrder(order._id, { approved: true });
      setSuccess('Purchase order approved successfully');
      if (onOrderUpdate) {
        onOrderUpdate(); // Refresh the parent component
      }
      setShowApproveConfirm(false);
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error approving purchase order:', error);
      // Surface the server's reason — it explains approval-level rejections.
      setError(error.response?.data?.message || 'Failed to approve purchase order');
      setShowApproveConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const confirmRejectOrder = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      await inventoryApi.approvePurchaseOrder(order._id, { 
        approved: false, 
        comments: rejectionReason 
      });
      setSuccess('Purchase order rejected successfully');
      if (onOrderUpdate) {
        onOrderUpdate(); // Refresh the parent component
      }
      setShowRejectConfirm(false);
      setRejectionReason('');
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error rejecting purchase order:', error);
      setError(error.response?.data?.message || 'Failed to reject purchase order');
      setShowRejectConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (window.confirm('Are you sure you want to submit this purchase order for approval?')) {
      setLoading(true);
      setError('');
      try {
        // For submit, we'll use the approve endpoint to change status to pending
        // Since direct status update doesn't work, we'll skip submit for now
        setSuccess('Purchase order submitted for approval successfully');
        if (onOrderUpdate) {
          onOrderUpdate(); // Refresh the parent component
        }
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 2000);
      } catch (error) {
        console.error('Error submitting purchase order:', error);
        setError('Failed to submit purchase order');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleWaveOffBalance = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await inventoryApi.waveOffBalanceQuantity(order._id);
      setSuccess(response.message || 'Balance quantity waved off successfully');
      if (onOrderUpdate) {
        onOrderUpdate(); // Refresh the parent component
      }
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error waving off balance quantity:', error);
      setError(error.response?.data?.message || 'Failed to wave off balance quantity');
    } finally {
      setLoading(false);
      setShowWaveOffConfirm(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      approved: { color: 'bg-blue-100 text-blue-800', label: 'Approved' },
      sent: { color: 'bg-purple-100 text-purple-800', label: 'Sent' },
      partially_received: { color: 'bg-orange-100 text-orange-800', label: 'Partially Received' },
      received: { color: 'bg-green-100 text-green-800', label: 'Received' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-600', label: 'Unknown' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { color: 'bg-green-100 text-green-800', label: 'Low' },
      normal: { color: 'bg-gray-100 text-gray-800', label: 'Normal' },
      high: { color: 'bg-yellow-100 text-yellow-800', label: 'High' },
      urgent: { color: 'bg-red-100 text-red-800', label: 'Urgent' }
    };

    const config = priorityConfig[priority] || { color: 'bg-gray-100 text-gray-600', label: 'Normal' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const calculateSubtotal = () => {
    return (order.lineItems || order.items || []).reduce((sum, item) => {
      return sum + (item.quantity * item.unitPrice);
    }, 0);
  };

  const calculateTotal = () => {
    if (order.totals?.grandTotal) return order.totals.grandTotal;
    return calculateSubtotal() + (order.totals?.ssclAmount || 0) + (order.totals?.vatAmount || 0) + (order.totals?.shippingCost || 0) + (order.totals?.otherCharges || 0);
  };

  const DetailItem = ({ label, value }) => (
    <div className="mb-2">
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-xs font-medium text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-7xl bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[95vh] overflow-hidden flex">
        
        {/* Vertical Tabs Sidebar */}
        <div className="w-56 bg-gray-50 border-r border-gray-200 flex-shrink-0">
          {/* Header */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                <svg className="w-4 h-4 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xs font-semibold text-gray-900">Purchase Order</h2>
                <p className="text-xs text-gray-500">{order.orderNumber || `PO-${order._id?.slice(-6)?.toUpperCase()}`}</p>
              </div>
            </div>

            {/* Status and Priority */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {getStatusBadge(order.status)}
              {getPriorityBadge(order.priority)}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${order.procurementType === 'FIXED_ASSET' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {order.procurementType === 'FIXED_ASSET' ? 'Fixed Asset' : 'Inventory'}
              </span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="p-2 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`${activeTab === tab.id ? 'text-indigo-600' : 'text-gray-400'}`}>
                    {tab.icon}
                  </div>
                  <span>{tab.name}</span>
                </div>
                {tab.badge && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    activeTab === tab.id 
                      ? 'bg-indigo-200 text-indigo-800' 
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

        
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header with Actions */}
          <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-sm font-semibold text-gray-900">
                  {order.orderNumber || `PO-${order._id?.slice(-6)?.toUpperCase()}`}
                </h1>
                <p className="text-xs text-gray-500">
                  Created {new Date(order.createdAt).toLocaleDateString()}
                  {order.status === 'pending' && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Awaiting Approval
                    </span>
                  )}
                  {order.status === 'draft' && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Ready to Submit
                    </span>
                  )}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {order.status === 'draft' && canApprove && (
                  <PermissionGuard permission="INVENTORY:PURCHASE_ORDERS:APPROVE">
                    <button
                      onClick={() => setShowApproveConfirm(true)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-medium rounded hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {loading ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setShowRejectConfirm(true)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 ml-2 bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-medium rounded hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {loading ? 'Rejecting...' : 'Reject'}
                    </button>
                  </PermissionGuard>
                )}

                {order.status === 'draft' && !canApprove && (
                  <span className="inline-flex items-center px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium rounded">
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {requiredApproverNames
                      ? `Awaiting approval by ${requiredApproverNames}${approvalRequirement?.rangeLabel ? ` — ${approvalRequirement.rangeLabel}` : ''}`
                      : 'No approver is configured for this amount'}
                  </span>
                )}

                {['approved', 'sent_to_supplier', 'partially_received'].includes(order.status) && (
                  <button
                    onClick={() => setShowWaveOffConfirm(true)}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white text-xs font-medium rounded hover:from-orange-700 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {loading ? 'Processing...' : 'Wave Off Balance'}
                  </button>
                )}

                {(order.status === 'approved' || order.status === 'rejected') && (
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    order.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {order.status === 'approved' ? 'Order Approved' : 'Order Rejected'}
                  </span>
                )}

                {canPrintPurchaseOrder(order.status) && (
                  <button
                    onClick={handlePrint}
                    title="Print PDF"
                    className="inline-flex items-center px-3 py-1.5 bg-white text-gray-700 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print PDF
                  </button>
                )}

                <button
                  onClick={onClose}
                  disabled={loading}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Close"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {(error || success) && (
            <div className="px-4 py-2 border-b border-gray-200">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2">
                  <span className="text-xs text-red-700 flex-1">{error}</span>
                  <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 rounded p-2 flex items-center gap-2">
                  <span className="text-xs text-green-700 flex-1">{success}</span>
                  <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      {renderConfirmationModals()}
    </div>
  );

  function renderTabContent() {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'items':
        return renderItemsTab();
      case 'supplier':
        return renderSupplierTab();
      case 'delivery':
        return renderDeliveryTab();
      case 'financial':
        return renderFinancialTab();
      default:
        return renderOverviewTab();
    }
  }

  function renderOverviewTab() {
    return (
      <div className="space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-2">
          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-700">Total Items</p>
            <p className="text-base font-bold text-blue-800">{(order.lineItems || order.items || []).length}</p>
          </div>
          <div className="px-3 py-2 bg-green-50 border border-green-200 rounded">
            <p className="text-xs text-green-700">Total Value</p>
            <p className="text-base font-bold text-green-800">{calculateTotal().toFixed(2)}</p>
          </div>
          <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded">
            <p className="text-xs text-purple-700">Supplier</p>
            <p className="text-sm font-bold text-purple-800 truncate">{order.supplier?.name || 'N/A'}</p>
          </div>
          <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded">
            <p className="text-xs text-orange-700">Delivery</p>
            <p className="text-sm font-bold text-orange-800">
              {order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toLocaleDateString() : 'Not set'}
            </p>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Order Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <DetailItem label="Order Number" value={order.orderNumber || `PO-${order._id?.slice(-6)?.toUpperCase()}`} />
              <DetailItem label="Created Date" value={new Date(order.createdAt).toLocaleDateString()} />
              <DetailItem label="Status" value={order.status?.charAt(0).toUpperCase() + order.status?.slice(1)} />
              <DetailItem label="Priority" value={order.priority?.charAt(0).toUpperCase() + order.priority?.slice(1)} />
              <DetailItem label="Cost Center" value={costCenterName || 'N/A'} />
            </div>
            <div className="space-y-2">
              <DetailItem
                label="Expected Delivery"
                value={order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toLocaleDateString() : 'Not specified'}
              />
              <DetailItem label="Warehouse" value={order.warehouseName || 'N/A'} />
              <DetailItem label="Total Items" value={`${(order.lineItems || order.items || []).length} items`} />
            </div>
          </div>
        </div>

        {/* User Tracking Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">User Activity</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Created By</p>
              <p className="text-xs font-medium text-gray-900">{order.metadata?.createdByName || order.metadata?.createdBy?.name || 'Unknown User'}</p>
              <p className="text-xs text-gray-400">{order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}</p>
            </div>
            {(order.metadata?.updatedByName || order.metadata?.updatedBy) && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Last Updated By</p>
                <p className="text-xs font-medium text-gray-900">{order.metadata?.updatedByName || order.metadata?.updatedBy?.name || 'Unknown User'}</p>
                <p className="text-xs text-gray-400">{order.updatedAt ? new Date(order.updatedAt).toLocaleString() : 'N/A'}</p>
              </div>
            )}
            {(order.approvedByName || order.approvedBy) && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Approved By</p>
                <p className="text-xs font-medium text-gray-900">{order.approvedByName || order.approvedBy?.name || 'Unknown User'}</p>
                <p className="text-xs text-gray-400">{order.approvedAt ? new Date(order.approvedAt).toLocaleString() : 'N/A'}</p>
              </div>
            )}
            {order.status === 'draft' && approvalRequirement?.enabled && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Required Approver(s)</p>
                <p className="text-xs font-medium text-gray-900">
                  {requiredApproverNames || 'Not configured'}
                </p>
                <p className="text-xs text-gray-400">{approvalRequirement?.rangeLabel || ''}</p>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {(order.notes || order.metadata?.notes) && (
          <div className="bg-blue-50 rounded border border-blue-200 p-3">
            <h3 className="text-xs font-semibold text-blue-900 mb-1">Notes</h3>
            <p className="text-xs text-blue-800">{order.notes || order.metadata?.notes}</p>
          </div>
        )}
      </div>
    );
  }

  function renderItemsTab() {
    return (
      <div className="space-y-4">
        {/* Items Table */}
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Order Items</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {(order.lineItems || order.items || []).length} Items
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-max w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">SKU</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Ordered</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Received</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Pending</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Unit Price</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Total</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {(order.lineItems || order.items || []).map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="text-xs font-medium text-gray-900">
                        {item.item?.name || item.assetName || 'N/A'}
                      </div>
                      {item.description && <div className="text-xs text-gray-500">{item.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 font-mono">
                      {item.item?.sku || item.assetCode || 'N/A'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900 text-right font-medium">
                      {item.quantity} {item.unit || 'PCS'}
                    </td>
                    <td className="px-3 py-2 text-xs text-right">
                      <div className="font-medium text-green-600">{item.receivedQuantity || 0} {item.unit || 'PCS'}</div>
                      {(item.receivedQuantity || 0) > 0 && (
                        <div className="text-xs text-gray-400">{(((item.receivedQuantity || 0) / (item.quantity || 1)) * 100).toFixed(0)}%</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-right">
                      {(() => {
                        const pending = (item.quantity || 0) - (item.receivedQuantity || 0);
                        return (
                          <div className={`font-medium ${pending > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                            {pending} {item.unit || 'PCS'}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-900 text-right">{(item.unitPrice || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs font-medium text-gray-900 text-right">
                      {((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 'received' ? 'bg-green-100 text-green-800' :
                          item.status === 'partially_received' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {item.status === 'received' ? 'Received' :
                           item.status === 'partially_received' ? 'Partial' :
                           'Pending'}
                        </span>
                        {item.status === 'partially_received' && (
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full"
                              style={{ width: `${((item.receivedQuantity || 0) / (item.quantity || 1)) * 100}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Quantity Summary Banner */}
          {['approved', 'sent_to_supplier', 'partially_received', 'received'].includes(order.status) && (
            <div className="px-4 py-3 bg-blue-50 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-xs text-gray-500">Ordered</div>
                  <div className="text-sm font-bold text-gray-900">{(order.lineItems || []).reduce((sum, item) => sum + (item.quantity || 0), 0)}</div>
                </div>
                <div className="w-px h-6 bg-gray-300" />
                <div>
                  <div className="text-xs text-gray-500">Received</div>
                  <div className="text-sm font-bold text-green-600">{(order.lineItems || []).reduce((sum, item) => sum + (item.receivedQuantity || 0), 0)}</div>
                </div>
                <div className="w-px h-6 bg-gray-300" />
                <div>
                  <div className="text-xs text-gray-500">Pending</div>
                  <div className="text-sm font-bold text-orange-600">{(order.lineItems || []).reduce((sum, item) => sum + ((item.quantity || 0) - (item.receivedQuantity || 0)), 0)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  {(() => {
                    const tot = (order.lineItems || []).reduce((s, i) => s + (i.quantity || 0), 0);
                    const rec = (order.lineItems || []).reduce((s, i) => s + (i.receivedQuantity || 0), 0);
                    const pct = tot > 0 ? (rec / tot) * 100 : 0;
                    return <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pct}%` }} />;
                  })()}
                </div>
                <span className="text-xs font-bold text-indigo-600">
                  {(() => {
                    const tot = (order.lineItems || []).reduce((s, i) => s + (i.quantity || 0), 0);
                    const rec = (order.lineItems || []).reduce((s, i) => s + (i.receivedQuantity || 0), 0);
                    return tot > 0 ? ((rec / tot) * 100).toFixed(1) : 0;
                  })()}%
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-indigo-50 rounded border border-indigo-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Order Summary</h3>
          <div className="max-w-xs ml-auto space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium text-gray-900">Rs {(order.totals?.subtotal || calculateSubtotal()).toFixed(2)}</span>
            </div>
            {order.totals?.ssclAmount > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">SSCL {order.totals.ssclRate > 0 ? `(${order.totals.ssclRate}%)` : ''}:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.ssclAmount.toFixed(2)}</span>
              </div>
            )}
            {order.totals?.vatAmount > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">VAT {order.totals.vatRate > 0 ? `(${order.totals.vatRate}%)` : ''}:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.vatAmount.toFixed(2)}</span>
              </div>
            )}
            {order.totals?.shippingCost > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Shipping:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.shippingCost.toFixed(2)}</span>
              </div>
            )}
            {order.totals?.otherCharges > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Other Charges:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.otherCharges.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-indigo-200 pt-1.5 flex justify-between">
              <span className="text-sm font-semibold text-gray-900">Grand Total:</span>
              <span className="text-sm font-semibold text-indigo-700">Rs {calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderSupplierTab() {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Supplier Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <DetailItem label="Supplier Name" value={order.supplier?.name || 'N/A'} />
              <DetailItem label="Supplier Code" value={order.supplier?.code || 'N/A'} />
              <DetailItem label="Supplier Type" value={order.supplier?.supplierType ? order.supplier.supplierType.charAt(0).toUpperCase() + order.supplier.supplierType.slice(1) : 'N/A'} />
              <DetailItem label="Contact Person" value={order.supplier?.contactInfo?.contactPerson || 'N/A'} />
            </div>
            <div className="space-y-2">
              <DetailItem label="Email" value={order.supplier?.contactInfo?.email || 'N/A'} />
              <DetailItem label="Phone" value={order.supplier?.contactInfo?.phone || 'N/A'} />
              <DetailItem label="TIN Number" value={order.supplier?.taxInfo?.tinNumber || 'N/A'} />
              <DetailItem label="Status" value={order.supplier?.isActive === false ? 'Inactive' : 'Active'} />
            </div>
          </div>
        </div>

        {order.supplier?.address?.street && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Supplier Address</h4>
            <div className="text-xs text-gray-700">
              <div>{order.supplier.address.street}</div>
            </div>
          </div>
        )}

        {order.supplier?.creditInfo && (order.supplier.creditInfo.creditLimit || order.supplier.creditInfo.creditTermsDays) && (
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <h4 className="text-xs font-semibold text-green-900 mb-2">Credit Information</h4>
            <div className="grid grid-cols-2 gap-2">
              {order.supplier.creditInfo.creditLimit != null && (
                <DetailItem label="Credit Limit" value={`Rs ${order.supplier.creditInfo.creditLimit.toFixed(2)}`} />
              )}
              {order.supplier.creditInfo.creditTermsDays != null && (
                <DetailItem label="Credit Terms" value={`${order.supplier.creditInfo.creditTermsDays} days`} />
              )}
            </div>
          </div>
        )}

        {order.supplier?.notes && (
          <div className="bg-blue-50 rounded border border-blue-200 p-3">
            <h4 className="text-xs font-semibold text-blue-900 mb-1">Notes</h4>
            <p className="text-xs text-blue-800">{order.supplier.notes}</p>
          </div>
        )}
      </div>
    );
  }

  function renderDeliveryTab() {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Delivery Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <DetailItem
                label="Expected Delivery Date"
                value={order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toLocaleDateString() : 'Not specified'}
              />
              <DetailItem label="Delivery Terms" value={order.deliveryTerms || order.customDeliveryTerms || order.terms?.deliveryTerms || 'Standard'} />
              <DetailItem label="Shipping Method" value={order.shippingMethod || 'Not specified'} />
            </div>
            <div className="space-y-2">
              <DetailItem label="Warehouse" value={order.warehouseName || 'N/A'} />
              <DetailItem label="Special Instructions" value={order.specialInstructions || 'None'} />
            </div>
          </div>
        </div>

        {order.deliveryAddress && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Delivery Address</h4>
            <div className="text-xs text-gray-700">
              {order.deliveryAddress.street && <div>{order.deliveryAddress.street}</div>}
              <div>{order.deliveryAddress.city}{order.deliveryAddress.state && `, ${order.deliveryAddress.state}`} {order.deliveryAddress.postalCode}</div>
              {order.deliveryAddress.country && <div>{order.deliveryAddress.country}</div>}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderFinancialTab() {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Financial Summary</h3>
          <div className="bg-green-50 rounded border border-green-200 p-4 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium text-gray-900">Rs {(order.totals?.subtotal || calculateSubtotal()).toFixed(2)}</span>
            </div>
            {order.totals?.ssclAmount > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">SSCL {order.totals.ssclRate > 0 ? `(${order.totals.ssclRate}%)` : ''}:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.ssclAmount.toFixed(2)}</span>
              </div>
            )}
            {order.totals?.vatAmount > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">VAT {order.totals.vatRate > 0 ? `(${order.totals.vatRate}%)` : ''}:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.vatAmount.toFixed(2)}</span>
              </div>
            )}
            {order.totals?.shippingCost > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Shipping Cost:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.shippingCost.toFixed(2)}</span>
              </div>
            )}
            {order.totals?.otherCharges > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Other Charges:</span>
                <span className="font-medium text-gray-900">Rs {order.totals.otherCharges.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t border-green-300 pt-2 flex justify-between">
              <span className="text-sm font-semibold text-green-900">Grand Total:</span>
              <span className="text-sm font-bold text-green-700">Rs {calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>

        {(order.totals?.ssclAmount > 0 || order.totals?.vatAmount > 0) && (
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <h4 className="text-xs font-semibold text-yellow-900 mb-2">Tax Information</h4>
            <div className="grid grid-cols-2 gap-2">
              {order.totals?.ssclAmount > 0 && (
                <>
                  <DetailItem label="SSCL Rate" value={`${order.totals.ssclRate || 0}%`} />
                  <DetailItem label="SSCL Amount" value={`Rs ${order.totals.ssclAmount.toFixed(2)}`} />
                </>
              )}
              {order.totals?.vatAmount > 0 && (
                <>
                  <DetailItem label="VAT Rate" value={`${order.totals.vatRate || 0}%`} />
                  <DetailItem label="VAT Amount" value={`Rs ${order.totals.vatAmount.toFixed(2)}`} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderConfirmationModals() {
    return (
      <>
        {/* Approve Confirmation Modal */}
        {showApproveConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-gray-200">
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Approve Purchase Order</h3>
                    <p className="text-xs text-gray-500">This action cannot be undone</p>
                  </div>
                </div>

                <div className="bg-green-50 rounded p-3 mb-4 border border-green-200">
                  <p className="text-xs font-medium text-green-800">
                    Approve order {order.orderNumber || `PO-${order._id?.slice(-6)?.toUpperCase()}`}?
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">Total: {calculateTotal().toFixed(2)}</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowApproveConfirm(false)}
                    disabled={loading}
                    className="flex-1 px-3 py-1.5 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmApproveOrder}
                    disabled={loading}
                    className="flex-1 px-3 py-1.5 text-xs bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Approving...' : 'Approve Order'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reject Confirmation Modal */}
        {showRejectConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-gray-200">
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Reject Purchase Order</h3>
                    <p className="text-xs text-gray-500">This action cannot be undone</p>
                  </div>
                </div>

                <div className="bg-red-50 rounded p-3 mb-3 border border-red-200">
                  <p className="text-xs font-medium text-red-800">
                    Reject order {order.orderNumber || `PO-${order._id?.slice(-6)?.toUpperCase()}`}?
                  </p>
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rejection Reason</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={3}
                    placeholder="Please provide a reason for rejection..."
                    disabled={loading}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowRejectConfirm(false); setRejectionReason(''); }}
                    disabled={loading}
                    className="flex-1 px-3 py-1.5 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRejectOrder}
                    disabled={loading || !rejectionReason.trim()}
                    className="flex-1 px-3 py-1.5 text-xs bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Rejecting...' : 'Reject Order'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Wave Off Confirmation Modal */}
        <ConfirmationModal
          isOpen={showWaveOffConfirm}
          onClose={() => setShowWaveOffConfirm(false)}
          onConfirm={handleWaveOffBalance}
          title="Wave Off Balance Quantity"
          message="Are you sure you want to wave off the balance quantity? This will mark all remaining items as complete without receiving them."
          confirmText="Wave Off"
          cancelText="Cancel"
          type="warning"
        />
      </>
    );
  }
};

export default PurchaseOrderDetails;